/**
 * Analytics and Reporting Models
 * TypeScript interfaces matching backend analytics schemas
 */

// Enums
export enum GroupBy {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month'
}

export enum ReportType {
  OVERVIEW = 'overview',
  DOCUMENTS = 'documents',
  API_USAGE = 'api-usage',
  COSTS = 'costs'
}

export enum ExportFormat {
  CSV = 'csv',
  PDF = 'pdf',
  EXCEL = 'excel'
}

// Request Interfaces
export interface DateRangeRequest {
  start_date?: Date;
  end_date?: Date;
}

export interface DocumentAnalyticsRequest extends DateRangeRequest {
  group_by?: GroupBy;
}

export interface ApiUsageAnalyticsRequest extends DateRangeRequest {
  group_by?: GroupBy;
}

export interface CostAnalyticsRequest extends DateRangeRequest {
  group_by?: GroupBy;
}

export interface ExportRequest extends DateRangeRequest {
  report_type: ReportType;
  format: ExportFormat;
}

// Response Interfaces
export interface AnalyticsOverview {
  total_documents: number;
  success_rate: number;
  total_api_calls: number;
  total_costs: number;
  active_webhooks: number;
  error_rate: number;
  period_start: Date;
  period_end: Date;
}

export interface DocumentMetrics {
  date: Date;
  total_documents: number;
  successful_documents: number;
  failed_documents: number;
  average_confidence: number;
  average_processing_time: number;
}

export interface DocumentAnalyticsResponse {
  total_documents: number;
  successful_documents: number;
  failed_documents: number;
  average_confidence: number;
  period_start: Date;
  period_end: Date;
  metrics: DocumentMetrics[];
}

export interface ApiUsageMetrics {
  date: Date;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  rate_limit_hits: number;
  endpoints?: Record<string, number>;
}

export interface ApiUsageAnalyticsResponse {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  rate_limit_hits: number;
  period_start: Date;
  period_end: Date;
  metrics: ApiUsageMetrics[];
}

export interface CostMetrics {
  date: Date;
  total_cost: number;
  openai_cost: number;
  claude_cost: number;
  gemini_cost: number;
  document_count: number;
}

export interface CostAnalyticsResponse {
  total_cost: number;
  period_start: Date;
  period_end: Date;
  metrics: CostMetrics[];
}

export interface TemplateUsageMetrics {
  template_id: string;
  template_name: string;
  document_type: string;
  usage_count: number;
  success_rate: number;
  average_confidence: number;
  total_cost: number;
}

export interface TemplateAnalyticsResponse {
  templates: TemplateUsageMetrics[];
  total_templates: number;
  start_date: Date;
  end_date: Date;
}

export interface ProviderUsageMetrics {
  provider: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  success_rate: number;
  average_response_time: number;
  total_cost: number;
}

export interface ProviderAnalyticsResponse {
  providers: ProviderUsageMetrics[];
  total_requests: number;
  start_date: Date;
  end_date: Date;
}

export interface ExportResponse {
  file_url: string;
  filename: string;
  format: ExportFormat;
  generated_at: Date;
}

// Date Range Preset
export interface DateRangePreset {
  label: string;
  value: string;
  start_date: Date;
  end_date: Date;
}

// Chart Data Interfaces
export interface ChartDataPoint {
  label: string;
  value: number;
  date?: Date;
}

export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string;
  fill?: boolean;
}

export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

// Analytics State Interface (for NgRx)
export interface AnalyticsState {
  overview: AnalyticsOverview | null;
  documentAnalytics: DocumentAnalyticsResponse | null;
  apiUsageAnalytics: ApiUsageAnalyticsResponse | null;
  costAnalytics: CostAnalyticsResponse | null;
  templateAnalytics: TemplateAnalyticsResponse | null;
  providerAnalytics: ProviderAnalyticsResponse | null;
  dateRange: DateRangeRequest;
  groupBy: GroupBy;
  loading: boolean;
  error: string | null;
}

